// reCAPTCHA v3, for the register form only.
//
// The site key is checked-in and public ON PURPOSE — that is the whole design
// of a reCAPTCHA site key, paired with Google's own domain allowlist and a
// secret key that never leaves the server. Nothing here is sensitive.
//
// SITE_KEY is blank until deployed to production; leave it blank locally and
// this module quietly does nothing, matching the server's own fail-open
// posture in recaptcha.ts.
const SITE_KEY = '6LdyZKcqAAAAAPmNJfcqSFpKoYH2wNcPC_ODWufw';

declare global {
  interface Window {
    grecaptcha?: {
      ready(cb: () => void): void;
      execute(siteKey: string, opts: { action: string }): Promise<string>;
    };
  }
}

let scriptLoad: Promise<void> | undefined;

/**
 * Start loading the script as soon as a CAPTCHA-protected page mounts, not at
 * submit time. v3's score is partly behavioral, so giving it the page's full
 * on-screen time to work with (and showing the required badge promptly,
 * rather than having it pop in right as the page navigates away) both depend
 * on this running early. Safe to call more than once; the load is cached.
 */
export function preloadRecaptcha(): void {
  if (SITE_KEY) void loadScript();
}

function loadScript(): Promise<void> {
  if (scriptLoad) return scriptLoad;
  scriptLoad = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('recaptcha script failed to load'));
    document.head.append(script);
  });
  return scriptLoad;
}

/**
 * A fresh token for one action, or undefined if anything about reCAPTCHA is
 * unavailable — no site key, a blocked script, a slow network. The server
 * treats a missing token as "skip the check," never as a reason to refuse a
 * signup, so failing here is always safe.
 */
export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  if (!SITE_KEY) return undefined;
  try {
    await loadScript();
    return await new Promise<string>((resolve, reject) => {
      window.grecaptcha!.ready(() => {
        window.grecaptcha!.execute(SITE_KEY, { action }).then(resolve, reject);
      });
    });
  } catch {
    return undefined;
  }
}
